/**
 * lessonPlanSerializer.ts
 * --------------------------------------------------------------------------
 * Chuyển đổi 1 `LessonPlan` thành Markdown có cấu trúc rõ ràng (heading,
 * bold, bullet) — định dạng được tối ưu để các mô hình AI hiểu trọng tâm.
 *
 * Lý do dùng MARKDOWN thay vì JSON.stringify:
 *  - Mô hình LLM được huấn luyện rất nhiều trên markdown → bám sát heading,
 *    danh sách, và emphasis tốt hơn nhiều so với JSON thô.
 *  - Markdown ngắn gọn hơn JSON (giảm token), tránh `"key":"value"` lặp.
 *  - Người dùng cũng đọc được — tiện debug và xuất sang chia sẻ.
 *
 * Có 3 hàm chính:
 *  - `lessonPlanStepToMarkdown(step, index)`  : 1 hoạt động đơn lẻ
 *  - `lessonPlanToMarkdown(plan)`             : toàn bộ giáo án
 *  - `lessonPlanFocusedMarkdown(plan, idx?)`  : focus 1 hoạt động + tóm tắt
 *                                                các hoạt động khác (dùng
 *                                                khi mở suggestedAction từ 1
 *                                                step cụ thể)
 */

import type { LessonPlan, LessonPlanStep } from '../types';

/** Cắt bớt nội dung quá dài (giữ token ngắn cho AI và URL). */
const truncate = (s: string | undefined | null, max: number): string => {
  if (!s) return '';
  const v = String(s).trim();
  if (v.length <= max) return v;
  return v.slice(0, max - 1).trimEnd() + '…';
};

/** Ghép một mảng string thành bullet list markdown (đã trim, lọc rỗng). */
const toBullets = (items: unknown, prefix = '- '): string => {
  if (!Array.isArray(items)) return '';
  return items
    .map(i => (typeof i === 'string' ? i.trim() : ''))
    .filter(Boolean)
    .map(i => `${prefix}${i}`)
    .join('\n');
};

/**
 * Chuyển 1 hoạt động (step) trong giáo án sang markdown.
 * Hỗ trợ cả schema mới CV 5512 (objective/content/product/implementationRows)
 * lẫn schema cũ basic (teacherActivity/studentActivity/teachingAids).
 */
export function lessonPlanStepToMarkdown(step: LessonPlanStep, index?: number): string {
  if (!step || typeof step !== 'object') return '';
  const lines: string[] = [];
  const heading = step.step?.trim() || (typeof index === 'number' ? `Hoạt động ${index + 1}` : 'Hoạt động');
  lines.push(`### ${heading}`);
  if (step.duration) lines.push(`*Thời lượng dự kiến: ${step.duration}*`);

  // Schema CV 5512
  if (step.objective) lines.push(`\n**a) Mục tiêu**\n${step.objective.trim()}`);
  if (step.content) lines.push(`\n**b) Nội dung**\n${step.content.trim()}`);
  if (step.product) lines.push(`\n**c) Sản phẩm**\n${step.product.trim()}`);

  // Bảng tổ chức thực hiện (2 cột)
  if (Array.isArray(step.implementationRows) && step.implementationRows.length > 0) {
    lines.push(`\n**d) Tổ chức thực hiện**`);
    for (const row of step.implementationRows) {
      const phase = (row?.phase || 'Bước').toString().trim();
      lines.push(`\n#### ${phase}`);
      const tsa = toBullets(row?.teacherStudentActivity, '- _GV/HS:_ ');
      const ep = toBullets(row?.expectedProduct, '- _Sản phẩm:_ ');
      if (tsa) lines.push(tsa);
      if (ep) lines.push(ep);
    }
  } else if (step.implementation) {
    lines.push(`\n**d) Tổ chức thực hiện**\n${step.implementation.trim()}`);
  }

  // Schema cũ (basic) — chỉ in nếu CHƯA in mục tiêu mới (tránh trùng)
  if (!step.objective && step.teacherActivity) {
    lines.push(`\n**Hoạt động của giáo viên**\n${step.teacherActivity.trim()}`);
  }
  if (!step.objective && step.studentActivity) {
    lines.push(`\n**Hoạt động của học sinh**\n${step.studentActivity.trim()}`);
  }
  if (step.teachingAids) {
    lines.push(`\n**Đồ dùng dạy học:** ${step.teachingAids.trim()}`);
  }
  if (Array.isArray(step.competencyGoals) && step.competencyGoals.length > 0) {
    lines.push(`\n**Mục tiêu năng lực:**\n${toBullets(step.competencyGoals)}`);
  }

  // Tài nguyên gợi ý
  if (Array.isArray(step.resourceSuggestions?.imagePrompts) && step.resourceSuggestions.imagePrompts.length > 0) {
    lines.push(`\n**Hình ảnh minh hoạ gợi ý:**\n${toBullets(step.resourceSuggestions.imagePrompts)}`);
  }

  return lines.join('\n');
}

/**
 * Chuyển toàn bộ giáo án sang markdown đầy đủ.
 * Dùng khi cần truyền nguyên giáo án làm context cho AI.
 */
export function lessonPlanToMarkdown(plan: LessonPlan): string {
  if (!plan || typeof plan !== 'object') return '';
  const out: string[] = [];

  out.push(`# ${plan.title?.trim() || 'Giáo án'}`);

  // Metadata
  const meta: string[] = [];
  if (plan.subject) meta.push(`**Môn học:** ${plan.subject}`);
  if (plan.gradeLevel) meta.push(`**Cấp học:** ${plan.gradeLevel}`);
  if (plan.classLevel) meta.push(`**Lớp:** ${plan.classLevel}`);
  if (plan.textbookSet) meta.push(`**Bộ sách:** ${plan.textbookSet}`);
  if (plan.teachingDuration) meta.push(`**Thời lượng:** ${plan.teachingDuration} phút`);
  if (plan.model) meta.push(`**Mô hình giảng dạy:** ${plan.model}`);
  if (meta.length) out.push(meta.join(' • '));

  // I. Mục tiêu
  out.push('\n## I. MỤC TIÊU');
  if (plan.knowledgeObjective) out.push(`### 1. Về kiến thức\n${plan.knowledgeObjective.trim()}`);
  if (plan.competencyObjective) out.push(`\n### 2. Về năng lực\n${plan.competencyObjective.trim()}`);
  if (plan.qualityObjective) out.push(`\n### 3. Về phẩm chất\n${plan.qualityObjective.trim()}`);
  if (plan.aiIntegrationNote) out.push(`\n### Tích hợp năng lực AI (CV 3439)\n${plan.aiIntegrationNote.trim()}`);
  if (plan.digitalCompetencyNote) out.push(`\n### Tích hợp năng lực số (CV 3456)\n${plan.digitalCompetencyNote.trim()}`);

  // II. Thiết bị
  if (plan.equipmentAndMaterials) {
    out.push(`\n## II. THIẾT BỊ DẠY HỌC VÀ HỌC LIỆU\n${plan.equipmentAndMaterials.trim()}`);
  }

  // III. Tiến trình
  out.push('\n## III. TIẾN TRÌNH DẠY HỌC');
  if (Array.isArray(plan.steps)) {
    plan.steps.forEach((s, i) => {
      out.push('\n' + lessonPlanStepToMarkdown(s, i));
    });
  }

  return out.join('\n');
}

/**
 * Markdown tập trung vào 1 hoạt động cụ thể, kèm:
 *  - Mục tiêu tổng quát (làm bối cảnh)
 *  - Hoạt động trọng tâm (đầy đủ chi tiết)
 *  - Tóm tắt 1 dòng cho các hoạt động khác (giữ AI hiểu xếp lớp tổng thể)
 *
 * Dùng khi giáo viên bấm 1 suggestedAction gắn với 1 step cụ thể —
 * đưa lên cho AI cần hiểu CHÍNH hoạt động đó nhưng vẫn biết bối cảnh.
 *
 * @param plan Giáo án nguồn
 * @param focusStepIndex Chỉ số step cần focus (nếu undefined thì in toàn bộ)
 */
export function lessonPlanFocusedMarkdown(plan: LessonPlan, focusStepIndex?: number): string {
  if (!plan) return '';
  if (typeof focusStepIndex !== 'number' || !Array.isArray(plan.steps) || !plan.steps[focusStepIndex]) {
    return lessonPlanToMarkdown(plan);
  }

  const out: string[] = [];

  out.push(`# Giáo án nguồn: ${plan.title?.trim() || ''}`);
  const metaLine: string[] = [];
  if (plan.subject) metaLine.push(`Môn ${plan.subject}`);
  if (plan.gradeLevel) metaLine.push(plan.gradeLevel);
  if (plan.classLevel) metaLine.push(`(${plan.classLevel})`);
  if (plan.textbookSet) metaLine.push(plan.textbookSet);
  if (plan.teachingDuration) metaLine.push(`${plan.teachingDuration} phút`);
  if (metaLine.length) out.push(`*${metaLine.join(' • ')}*`);

  if (plan.knowledgeObjective || plan.competencyObjective) {
    out.push(`\n## Mục tiêu tổng quát của bài`);
    if (plan.knowledgeObjective) out.push(`**Kiến thức:** ${truncate(plan.knowledgeObjective, 600)}`);
    if (plan.competencyObjective) out.push(`\n**Năng lực:** ${truncate(plan.competencyObjective, 600)}`);
    if (plan.qualityObjective) out.push(`\n**Phẩm chất:** ${truncate(plan.qualityObjective, 400)}`);
  }

  if (plan.equipmentAndMaterials) {
    out.push(`\n**Thiết bị/học liệu:** ${truncate(plan.equipmentAndMaterials, 400)}`);
  }

  out.push(`\n## ⭐ HOẠT ĐỘNG TRỌNG TÂM (cần bám sát khi sinh nội dung)`);
  out.push(lessonPlanStepToMarkdown(plan.steps[focusStepIndex], focusStepIndex));

  // Bối cảnh các hoạt động khác (1 dòng / hoạt động)
  const others = plan.steps
    .map((s, i) => ({ s, i }))
    .filter(({ i }) => i !== focusStepIndex);
  if (others.length > 0) {
    out.push(`\n## Bối cảnh các hoạt động khác trong cùng bài (tham khảo)`);
    for (const { s, i } of others) {
      const summary = truncate(s.objective || s.content || s.teacherActivity || '', 180);
      out.push(`- **Hoạt động ${i + 1} – ${s.step || ''}:** ${summary}`);
    }
  }

  return out.join('\n');
}
